import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors, getAccountChartColor } from "../../lib/colors"
import StatCard from "../../components/analytics/StatCard"
import BarChart from "../../components/analytics/BarChart"
import PostGrid from "../../components/analytics/PostGrid"
import SummaryTable from "../../components/analytics/SummaryTable"
import PeriodSelectorModal from "../../components/analytics/PeriodSelectorModal"
import { readStoredPeriods, presetToRange } from "../../lib/dateRange"
import type { PeriodEntry } from "../../lib/dateRange"
import { api } from "../../lib/api"
import type { StoriesData, StoryRow, ChartData } from "../../types/analytics"
import { useSelectedAccount } from "../../contexts/AccountContext"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import FreeWindowNotice from "../../components/account/FreeWindowNotice"
import { useEarliestAccountDate } from "../../hooks/useEarliestAccountDate"
import { PAGE_TITLES } from "../../lib/navItems"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import "../../styles/analytics.scss"

const STORAGE_KEY = "lyto.storiesPeriod"

const SUMMARY_COLUMNS = [
  { key: "posts",          label: "投稿数" },
  { key: "frequency",      label: "投稿頻度" },
  { key: "reach",          label: "リーチ" },
  { key: "replies",        label: "返信" },
  { key: "profile_visits", label: "プロフィールアクセス" },
]

const fmtDateRange = (p: PeriodEntry) =>
  `${p.startDate.replace(/-/g, "/")} 〜 ${p.endDate.replace(/-/g, "/")}`

const StoriesPage = () => {
  const { selectedAccountIds, organizations, setSingleAccountOnly } = useSelectedAccount()
  const earliestDate = useEarliestAccountDate()
  const navigate = useNavigate()
  const reachChartRef = useRef<HTMLDivElement>(null)

  const freeWindowAccount = selectedAccountIds
    .map(id => organizations.flatMap(o => o.accounts).find(a => a.id === id))
    .find(a => a?.plan === "free")

  const accountColorMap = useMemo(() => {
    const map: Record<string, string> = {}
    selectedAccountIds.forEach((id, i) => {
      const account = organizations.flatMap(o => o.accounts).find(a => a.id === id)
      if (account) map[account.username] = getAccountChartColor(i).border
    })
    return map
  }, [selectedAccountIds, organizations])

  const applyAccountColors = (chartData: ChartData): ChartData => ({
    ...chartData,
    datasets: chartData.datasets.map((ds, i) => {
      const c = getAccountChartColor(i)
      return { ...ds, backgroundColor: ds.backgroundColor ?? c.bg, borderColor: ds.borderColor ?? c.border }
    }),
  })

  const [dataByPeriod, setDataByPeriod] = useState<(StoriesData | null)[]>([null])
  const [loading, setLoading]           = useState(true)
  const [periods, setPeriods]           = useState<PeriodEntry[]>(() => readStoredPeriods(STORAGE_KEY))
  const [refreshingId, setRefreshingId] = useState<number | null>(null)

  useEffect(() => {
    if (!earliestDate) return
    setPeriods(prev => {
      if (prev[0]?.preset !== "all") return prev
      return [{ ...prev[0], ...presetToRange("all", earliestDate) }, ...prev.slice(1)]
    })
  }, [earliestDate])

  useEffect(() => {
    setSingleAccountOnly(periods.length > 1)
  }, [periods, setSingleAccountOnly])

  const fetchData = useCallback(async () => {
    if (selectedAccountIds.length === 0) { setLoading(false); return }
    setLoading(true)
    try {
      const results = await Promise.all(
        periods.map(p => {
          const params = new URLSearchParams({ start_date: p.startDate, end_date: p.endDate })
          selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
          return api.get<StoriesData>(`/analytics/stories?${params}`).catch(() => null)
        })
      )
      setDataByPeriod(results)
    } catch {
    } finally {
      setLoading(false)
    }
  }, [selectedAccountIds, periods])

  useEffect(() => { fetchData() }, [fetchData])

  const handleRefresh = async (postId: number) => {
    setRefreshingId(postId)
    try {
      await api.post(`/analytics/posts/${postId}/refresh`, {})
    } finally {
      setRefreshingId(null)
    }
  }

  const combinedReachChart = useMemo(() => {
    const p0Data = dataByPeriod[0]
    if (!p0Data) return { labels: [] as string[], datasets: [] }
    if (dataByPeriod.length <= 1) return applyAccountColors(p0Data.reach_trend)

    const p0Chart = applyAccountColors(p0Data.reach_trend)
    return {
      labels: p0Chart.labels,
      datasets: dataByPeriod.flatMap((data, pi) => {
        if (!data || pi >= periods.length) return []
        const chart = applyAccountColors(data.reach_trend)
        const prefix = `${fmtDateRange(periods[pi])} | `
        return chart.datasets.map((ds, di) => {
          const c = getAccountChartColor(pi * p0Chart.datasets.length + di)
          return { ...ds, label: `${prefix}${ds.label}`, borderColor: c.border, backgroundColor: c.bg }
        })
      }),
    }
  }, [dataByPeriod, periods])

  const fmt = (v: number | null) => v != null ? v.toLocaleString() : "—"
  const data0 = dataByPeriod[0]

  return (
    <AppLayout pageTitle={PAGE_TITLES["/stories"]}>
      <div className="analytics">
        {freeWindowAccount && <FreeWindowNotice days={freeWindowAccount.features.free_window_days} />}
        {selectedAccountIds.length === 0 ? (
          <AccountSelectionNotice />
        ) : (
          <>
            <div className="analytics__header">
              <PeriodSelectorModal
                periods={periods}
                onChange={setPeriods}
                storageKey={STORAGE_KEY}
                allTimeStart={earliestDate}
                maxDays={freeWindowAccount?.features.free_window_days}
              />
            </div>

            {loading ? (
              <p style={{ color: colors.gray400 }}>読み込み中…</p>
            ) : !data0 ? (
              <p style={{ color: colors.gray400 }}>データを取得できませんでした</p>
            ) : (
              <>
                {/* StatCards - メイン期間のみ */}
                <div className="analytics__stats-grid">
                  <StatCard label="投稿数"               stat={data0.stats.posts}          unit="件" />
                  <StatCard label="リーチ"               stat={data0.stats.reach} />
                  <StatCard label="返信"                 stat={data0.stats.replies} />
                  <StatCard label="プロフィールアクセス" stat={data0.stats.profile_visits} />
                </div>

                {/* リーチ推移 - 全期間を1チャートに統合 */}
                {combinedReachChart.labels.length > 0 && (
                  <div className="analytics__section">
                    <div className="chart-card">
                      <div className="chart-card__header">
                        <span className="chart-card__title">リーチ推移</span>
                        <div className="chart-card__actions">
                          <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(combinedReachChart), `stories_reach_trend_${todayStr()}.csv`)}>↓ CSV</button>
                          <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(reachChartRef.current, `stories_reach_trend_${todayStr()}.png`)}>↓ PNG</button>
                        </div>
                      </div>
                      <div className="chart-card__canvas-wrap" ref={reachChartRef}>
                        <BarChart data={combinedReachChart} dateAxis />
                      </div>
                    </div>
                  </div>
                )}

                {/* サマリーテーブル - メイン期間のみ */}
                <div className="analytics__section">
                  <SummaryTable
                    rows={data0.summary}
                    columns={SUMMARY_COLUMNS}
                    title="サマリー"
                    filePrefix="stories_summary"
                  />
                </div>

                {/* 投稿グリッド - メイン期間のみ */}
                <div className="analytics__section">
                  <div className="analytics__section-title">投稿一覧</div>
                  <PostGrid
                    posts={data0.posts.map((p: StoryRow) => ({
                      id:            p.id,
                      thumbnail_url: null,
                      video_url:     p.video_url,
                      media_type:    p.media_type,
                      caption:       p.caption,
                      posted_at:     p.posted_at,
                      metric_label:  "リーチ",
                      metric_value:  p.reach,
                      username:      p.username,
                      color:         p.username ? accountColorMap[p.username] : undefined,
                    }))}
                    aspect="vertical"
                    onPostClick={(id, accountId) => navigate(`/stories/${id}`, { state: { accountId } })}
                  />
                </div>

                {/* 投稿詳細テーブル - メイン期間のみ */}
                <div className="analytics__section">
                  <div className="posts-table">
                    <div className="posts-table__header">
                      <span className="posts-table__title">投稿一覧</span>
                      <button
                        className="summary-table__csv-btn"
                        onClick={() => downloadCsv(
                          [
                            ["キャプション", "ユーザー名", "投稿日時", "リーチ", "返信", "プロフィールアクセス"],
                            ...data0.posts.map((p: StoryRow) => [
                              p.caption ?? "", p.username ?? "", p.posted_at ?? "",
                              p.reach, p.replies, p.profile_visits,
                            ]),
                          ],
                          `stories_posts_${todayStr()}.csv`
                        )}
                      >
                        ↓ CSV
                      </button>
                    </div>
                    <div className="posts-table__wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>投稿</th>
                            <th>リーチ</th>
                            <th>返信</th>
                            <th>プロフィールアクセス</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {data0.posts.map((post: StoryRow) => (
                            <tr key={post.id} className="posts-table__row--clickable" onClick={() => navigate(`/stories/${post.id}`, { state: { accountId: post.instagram_account_id } })}>
                              <td>
                                <div className="posts-table__thumb-cell">
                                  {post.video_url && post.media_type === "VIDEO"
                                    ? <video src={post.video_url} autoPlay loop muted playsInline className="posts-table__thumbnail" />
                                    : post.video_url
                                      ? <img src={post.video_url} alt="" className="posts-table__thumbnail" />
                                      : <div className="posts-table__no-thumb">📷</div>
                                  }
                                  <div className="posts-table__thumb-meta">
                                    {post.username && (
                                      <span
                                        className="posts-table__username"
                                        style={{ background: accountColorMap[post.username] ?? colors.gray400 }}
                                      >
                                        @{post.username}
                                      </span>
                                    )}
                                    <span className="posts-table__caption">{post.caption ?? "—"}</span>
                                  </div>
                                </div>
                              </td>
                              <td className={post.reach == null ? "null-value" : ""}>{fmt(post.reach)}</td>
                              <td className={post.replies == null ? "null-value" : ""}>{fmt(post.replies)}</td>
                              <td className={post.profile_visits == null ? "null-value" : ""}>{fmt(post.profile_visits)}</td>
                              <td>
                                {post.can_refresh && (
                                  <button
                                    className="posts-table__refresh-btn"
                                    disabled={refreshingId === post.id}
                                    onClick={e => { e.stopPropagation(); handleRefresh(post.id) }}
                                  >
                                    {refreshingId === post.id ? "…" : "更新"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default StoriesPage
